import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { listAdminEvents, deleteAdminEvent } from "../../lib/adminApi";
import { API_URL } from "../../lib/utils";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800";

// IMPORTANT: your API_URL is usually http://host:port/api
// images are served from http://host:port/uploads/...
const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return FALLBACK_IMAGE;

  const clean = url.trim();
  if (!clean) return FALLBACK_IMAGE;

  // already absolute
  if (/^https?:\/\//i.test(clean)) return clean;

  // relative path from backend static mount
  if (clean.startsWith("/")) return `${BACKEND_ORIGIN}${clean}`;
  return `${BACKEND_ORIGIN}/${clean}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString();
};

const formatTimeRange = (event) => {
  const start = event?.start_time || event?.time || "";
  const end = event?.end_time || "";
  if (start && end) return `${start} - ${end}`;
  if (start) return start;
  return "TBD";
};

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await listAdminEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || "Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;
    try {
      await deleteAdminEvent(id);
      toast.success("Event deleted");
      setEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  return (
    <AdminLayout title="Manage Events" onRefresh={loadEvents}>
      <div className="mb-4 flex items-center justify-end">
        <Button asChild className="bg-amber-700 hover:bg-amber-800 rounded-xl">
          <Link to="/admin/events/new">Create Event</Link>
        </Button>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-40 rounded-2xl bg-stone-200 animate-pulse" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <Card className="rounded-2xl border-stone-200">
          <CardContent className="p-8 text-center text-stone-500">
            No events found. Create your first event.
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {events.map((event) => (
            <Card key={event.id} className="rounded-2xl border-stone-200 overflow-hidden">
              <div className="h-40 bg-stone-100">
                <img
                  src={normalizeImageUrl(event.image_url)}
                  alt={event.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_IMAGE;
                  }}
                />
              </div>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-stone-900">{event.title}</h3>
                  <Badge variant="secondary">{formatDate(event.date)}</Badge>
                </div>

                <p className="text-sm text-stone-600 mt-2">
                  {event.venue || event.location || "-"}
                </p>
                <p className="text-sm text-stone-500 mt-1">{formatTimeRange(event)}</p>
                <p className="text-sm text-stone-500 mt-1">
                  {event.organizer || event.organiser || "GLGIF"}
                </p>

                <div className="mt-4 flex items-center gap-2">
                  <Button asChild variant="outline" className="rounded-xl">
                    <Link to={`/admin/events/${event.id}/edit`}>Edit</Link>
                  </Button>
                  <Button
                    variant="destructive"
                    className="rounded-xl"
                    onClick={() => handleDelete(event.id)}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminEvents;
